import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

const DESIG_RANK = {
  "Dean": 0, "Professor-HOD": 1, "Professor - HOD": 1, "Professor": 2, "Associate Professor": 3,
  "Assistant Professor": 4, "Senior Resident": 5, "Junior Resident": 6,
  "Demonstrator": 7, "Tutor": 8,
};

const COLUMNS = [
  { key: "full_name", label: "Name" },
  { key: "ssmch_id", label: "SSMCH ID" },
  { key: "department", label: "Department" },
  { key: "designation", label: "Designation", rankSort: true },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "status", label: "Status" },
  { key: "joined_at", label: "Joined" },
];

export default function FacultyDrilldown({ department, designation, title, onClose }) {
  const navigate = useNavigate();
  const [faculty, setFaculty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    (async () => {
      try {
        const params = {};
        if (department) params.department = department;
        if (designation) params.designation = designation;
        const { data } = await api.get("/faculty/by-filter", { params });
        setFaculty(data);
      } catch {}
      setLoading(false);
    })();
  }, [department, designation]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  const sorted = useMemo(() => {
    if (!sortCol) return faculty;
    const colDef = COLUMNS.find(c => c.key === sortCol);
    const arr = [...faculty];
    arr.sort((a, b) => {
      let av, bv;
      if (colDef?.rankSort) {
        av = DESIG_RANK[a[sortCol]] ?? 99;
        bv = DESIG_RANK[b[sortCol]] ?? 99;
      } else {
        av = (a[sortCol] || "").toString().toLowerCase();
        bv = (b[sortCol] || "").toString().toLowerCase();
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [faculty, sortCol, sortDir]);

  const thStyle = { padding: '7px 10px', textAlign: 'left', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', fontSize: 12 };

  return (
    <div style={{position:'fixed',inset:0,zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.45)'}}
      onClick={onClose} data-testid="drilldown-overlay">
      <div style={{background:'var(--bg)',border:'1px solid var(--border)',borderRadius:14,padding:'24px 28px',width:'90%',maxWidth:860,maxHeight:'80vh',display:'flex',flexDirection:'column',boxShadow:'0 16px 48px rgba(0,0,0,.15)'}}
        onClick={e => e.stopPropagation()} data-testid="drilldown-modal">
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <div>
            <div style={{fontSize:16,fontWeight:600}}>{title}</div>
            <div style={{fontSize:12,color:'var(--text-3)',marginTop:2}}>
              {department && <span>{department}</span>}
              {department && designation && <span> · </span>}
              {designation && <span>{designation}</span>}
              {!loading && <span> · {faculty.length} faculty</span>}
            </div>
          </div>
          <button className="btn" onClick={onClose} style={{fontSize:12,padding:'4px 14px'}} data-testid="drilldown-close">Close</button>
        </div>
        <div style={{overflowY:'auto',flex:1}}>
          {loading ? (
            <div style={{padding:30,textAlign:'center'}}><div className="spinner"/></div>
          ) : faculty.length === 0 ? (
            <div style={{padding:30,textAlign:'center',color:'var(--text-3)',fontSize:13}}>No faculty found</div>
          ) : (
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}} data-testid="drilldown-table">
              <thead>
                <tr style={{background:'var(--bg-alt)',borderBottom:'1px solid var(--border)'}}>
                  {COLUMNS.map(col => (
                    <th key={col.key} onClick={() => toggleSort(col.key)} style={thStyle} data-testid={`drill-th-${col.key}`}
                      dangerouslySetInnerHTML={{__html: `${col.label}${sortCol === col.key ? (sortDir === 'asc' ? ' &#9650;' : ' &#9660;') : ' <span style="opacity:0.25;font-size:10px">&#8597;</span>'}`}}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map(f => (
                  <tr key={f.faculty_id} style={{borderBottom:'1px solid var(--line-soft)',cursor:'pointer'}}
                    onClick={() => { onClose(); navigate(`/profile/${f.faculty_id}`); }}
                    data-testid={`drill-row-${f.faculty_id}`}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-alt)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{padding:'7px 10px',fontWeight:500,color:'var(--accent)'}}>{f.title} {f.full_name}</td>
                    <td style={{padding:'7px 10px',fontFamily:'var(--mono)',fontSize:11}}>{f.ssmch_id}</td>
                    <td style={{padding:'7px 10px'}}>{f.department || '—'}</td>
                    <td style={{padding:'7px 10px'}}>{f.designation || '—'}</td>
                    <td style={{padding:'7px 10px',fontFamily:'var(--mono)',fontSize:11}}>{f.whatsapp || '—'}</td>
                    <td style={{padding:'7px 10px'}}><span className={`badge ${f.status === 'Active' ? 'ok' : f.status === 'Absconded' ? 'bad' : 'warn'}`}>{f.status}</span></td>
                    <td style={{padding:'7px 10px',fontSize:11,color:'var(--text-3)'}}>{f.joined_at || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
